/*!
 * Copyright 2017 by ChartIQ, Inc.
 * All rights reserved.
 */

import { forEach as asyncForEach } from "async";
import Logger from "../../../clients/logger";
import LauncherClient from "../../../clients/launcherClient";
import ConfigClient from "../../../clients/configClient";
import DistributedStoreClient from "../../../clients/distributedStoreClient";
import { BootTaskCallbackInterface } from "../types";
import AppDirectory from "./modules/AppDirectory";
import AppDClient from "./modules/AppDClient";
let clientInstance;
let appd;

const loadAppDInstalledComponentsTask = "loadAppDInstalledComponentsTask";
/**
 * Boot task that initializes any FDC3 components
 * @private
 */
export class LoadAppDInstalledComponentsTask {
	public async start(doneCallback: BootTaskCallbackInterface) {
		Logger.system.debug("LoadAppDInstalledComponentsTask start");

		const { data: appDirectoryEndpoint } = await ConfigClient.getValue({ field: "finsemble.appDirectoryEndpoint" });
		// If we don't have the config value, we don't need to load.
		if (appDirectoryEndpoint === null) {
			Logger.system.debug("LoadAppDInstalledComponentsTask appDirectoryEndpoint is not set. No components to load.");
			doneCallback(loadAppDInstalledComponentsTask, "bootTasks", "completed");
			return;
		}
		const { err: getStoreErr, data: response } = await DistributedStoreClient.getStore({
			store: "Finsemble-AppLauncher-Store",
			global: true,
			waitForPersistentStoreToLoad: true,
		});
		let installedComponents = {};
		if (getStoreErr) {
			Logger.system.debug("LoadAppDInstalledComponentsTask error", getStoreErr);
			doneCallback(loadAppDInstalledComponentsTask, "bootTasks", "completed");
			return;
		}
		// Load FDC3 components here
		if (!response?.values?.appDefinitions) {
			doneCallback(loadAppDInstalledComponentsTask, "bootTasks", "completed");
			return;
		}
		installedComponents = response.values.appDefinitions;

		asyncForEach(
			installedComponents,
			(component, done) => {
				this.lazyLoadAppD(appDirectoryEndpoint);
				// get the app info so we can load it into the launcher
				this.getApp(component.appID, (getAppErr, app) => {
					if (getAppErr) {
						console.error("There was an error loading from FDC3", component, getAppErr);
						done();
					}
					// register the component with the launcher service
					LauncherClient.registerComponent(
						{
							componentType: component.name,
							manifest: JSON.parse(app.manifest),
						},
						(registerComponentErr) => {
							Logger.system.debug("LoadAppDInstalledComponentsTask err", registerComponentErr);
							done();
						}
					);
				});
			},
			() => {
				Logger.system.debug("LoadAppDInstalledComponentsTask done");
				doneCallback(loadAppDInstalledComponentsTask, "bootTasks", "completed");
			}
		);
	}

	// This gets a specific app in FDC3 and returns the results
	private getApp(appID, cb = Function.prototype) {
		appd
			.get(appID)
			.then((app) => cb(null, app))
			.catch((err) => cb(err));
	}

	/**
	 * Instantiates classes needed to interact with the appD server.
	 */
	private lazyLoadAppD(appDEndpoint) {
		if (!clientInstance) clientInstance = new AppDClient({ url: appDEndpoint });
		if (!appd) appd = new AppDirectory(clientInstance);
	}
}
